import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase.config';
import { getAuth } from 'firebase/auth';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';

export const useUnreadReports = () => {
  const { currentCompany, loading } = useEnhancedCompany();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [unreadEmployeeReports, setUnreadEmployeeReports] = useState(0);
  const [unreadOwnerReports, setUnreadOwnerReports] = useState(0);
  const [isOwner, setIsOwner] = useState(false);

  // Check if user is owner
  useEffect(() => {
    const checkOwnership = async () => {
      if (loading || !currentCompany?.companyId || !currentUser?.uid) {
        setIsOwner(false);
        return;
      }

      try {
        // Check ownership directly from user_companies collection
        const { getDoc, doc } = await import('firebase/firestore');
        const userCompaniesRef = doc(db, 'user_companies', currentUser.uid);
        const userCompaniesDoc = await getDoc(userCompaniesRef);
        
        if (userCompaniesDoc.exists()) {
          const userData = userCompaniesDoc.data();
          const ownedCompanies = userData.owned_companies || [];
          
          // Check if current company is in owned_companies array
          const isOwnerUser = ownedCompanies.some(company => 
            company.companyId === currentCompany.companyId && company.role === 'owner'
          );
          
          setIsOwner(isOwnerUser);
        } else {
          setIsOwner(false);
        }
      } catch (error) {
        console.error('Error checking ownership:', error);
        setIsOwner(false);
      }
    };

    checkOwnership();
  }, [loading, currentCompany?.companyId, currentUser?.uid]);

  // Listen for unread reports from employees (for owner)
  useEffect(() => {
    if (loading || !currentCompany?.companyId || !isOwner) {
      setUnreadOwnerReports(0);
      return;
    }

    const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
    const q = query(reportsRef, where('isRead', '==', false));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadOwnerReports(snapshot.size);
    }, (error) => {
      console.error('Error loading unread reports:', error);
      setUnreadOwnerReports(0);
    });

    return () => unsubscribe();
  }, [loading, currentCompany?.companyId, isOwner]);

  // Listen for unread replies from owner (for employees)
  useEffect(() => {
    if (loading || !currentCompany?.companyId || !currentUser?.uid || isOwner) {
      setUnreadEmployeeReports(0);
      return;
    }

    const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
    const q = query(
      reportsRef,
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Count reports that have replies but haven't been marked as read by employee
      let unreadCount = 0;
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.ownerReply && !data.employeeReadReply) {
          unreadCount++;
        }
      });
      setUnreadEmployeeReports(unreadCount);
    }, (error) => {
      console.error('Error loading employee unread reports:', error);
      setUnreadEmployeeReports(0);
    });

    return () => unsubscribe();
  }, [loading, currentCompany?.companyId, currentUser?.uid, isOwner]);

  // Mark owner reports as read when owner views them
  const markOwnerReportsAsRead = async () => {
    if (!currentCompany?.companyId || !isOwner) return;

    try {
      const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
      const q = query(reportsRef, where('isRead', '==', false));

      const snapshot = await import('firebase/firestore').then(({ getDocs }) => getDocs(q));

      const updatePromises = snapshot.docs.map(doc =>
        import('firebase/firestore').then(({ updateDoc, doc: docRef }) =>
          updateDoc(docRef(db, 'companies', currentCompany.companyId, 'reports', doc.id), {
            isRead: true,
            readAt: new Date()
          })
        )
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking reports as read:', error);
    }
  };

  // Mark employee reply as read
  const markEmployeeReplyAsRead = async (reportId) => {
    if (!currentCompany?.companyId || !currentUser?.uid) return;

    try {
      await import('firebase/firestore').then(({ updateDoc, doc }) =>
        updateDoc(doc(db, 'companies', currentCompany.companyId, 'reports', reportId), {
          employeeReadReply: true,
          employeeReadAt: new Date()
        })
      );
    } catch (error) {
      console.error('Error marking reply as read:', error);
    }
  };

  return {
    unreadEmployeeReports,
    unreadOwnerReports,
    isOwner,
    markOwnerReportsAsRead,
    markEmployeeReplyAsRead
  };
};
